/**
 * NotificationPrefsPage — `/settings/notifications`
 *
 * Lets users mute specific notification kinds (DMs / mentions / room messages)
 * and set quiet hours. Also exposes the browser push subscription toggle so
 * everything related to push lives in one place.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bell, BellOff, Moon, MessageCircle, AtSign, Hash, Save, Loader2 } from 'lucide-react';
import usePushNotifications from '@/hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationPrefsPage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const push = usePushNotifications(token);

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const r = await axios.get(`${API}/settings/notifications`, { headers });
        setPrefs(r.data);
      } finally { setLoading(false); }
    };
    fetchPrefs();
  }, [headers]);

  const patch = (partial) => setPrefs(p => ({ ...p, ...partial }));
  const patchQH = (partial) => setPrefs(p => ({ ...p, quiet_hours: { ...p.quiet_hours, ...partial } }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/settings/notifications`, prefs, { headers });
      setPrefs(r.data);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save preferences');
    } finally { setSaving(false); }
  };

  const handleTogglePush = async () => {
    if (push.isSubscribed) {
      await push.unsubscribe();
      toast.success('Browser push disabled');
    } else {
      const r = await push.subscribe();
      if (r?.ok) toast.success('Browser push enabled');
      else if (r?.error) toast.error(r.error);
    }
  };

  // Prefill client timezone offset if never set
  useEffect(() => {
    if (prefs && prefs.quiet_hours && (prefs.quiet_hours.timezone_offset_minutes === 0 || prefs.quiet_hours.timezone_offset_minutes === undefined)) {
      const localOffset = -new Date().getTimezoneOffset();  // Date.getTimezoneOffset is inverse sign
      if (prefs.quiet_hours.timezone_offset_minutes !== localOffset) {
        patchQH({ timezone_offset_minutes: localOffset });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading || !prefs) {
    return <div className="p-6 text-sm text-[#9CA3AF]">Loading preferences…</div>;
  }

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[820px]" data-testid="notification-prefs-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" data-testid="page-title">Notifications</h1>
          <p className="text-sm text-[#6B7280]">Choose what pings you, where, and when.</p>
        </div>
      </div>

      {/* Browser Push toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {push.isSubscribed ? <Bell className="w-4 h-4 text-[#34C759]" /> : <BellOff className="w-4 h-4 text-[#9CA3AF]" />}
            Browser push
            {push.isSubscribed && <Badge className="bg-[#34C759]/15 text-[#34C759] text-[10px] ml-1">Active</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#4B5563]">
            Push notifications are sent per-browser. Enable on each device where you want to hear pings.
          </p>
          {!push.isSupported && (
            <p className="text-xs text-[#FF9500]">Your browser does not support push notifications.</p>
          )}
          {push.permission === 'denied' && (
            <p className="text-xs text-[#FF3B30]">Permission denied. Re-enable in browser site settings.</p>
          )}
          {push.isSupported && push.permission !== 'denied' && (
            <Button
              onClick={handleTogglePush}
              disabled={push.busy}
              className={push.isSubscribed ? 'bg-[#9CA3AF]' : 'bg-[#002FA7]'}
              data-testid="push-browser-toggle"
            >
              {push.busy && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
              {push.isSubscribed ? 'Disable browser push' : 'Enable browser push'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Event-type preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">What to notify me about</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <PrefRow
            icon={MessageCircle} color="#002FA7"
            title="Direct messages"
            desc="Push every time someone sends you a DM"
            checked={prefs.push_dms}
            onChange={(v) => patch({ push_dms: v })}
            testId="pref-dms"
          />
          <PrefRow
            icon={AtSign} color="#AF52DE"
            title="@Mentions"
            desc="Push when you're tagged in any room"
            checked={prefs.push_mentions}
            onChange={(v) => patch({ push_mentions: v })}
            testId="pref-mentions"
          />
          <PrefRow
            icon={Hash} color="#34C759"
            title="Room messages"
            desc="Push on every non-mention message in rooms you're in (noisy — off by default)"
            checked={prefs.push_room_messages}
            onChange={(v) => patch({ push_room_messages: v })}
            testId="pref-room"
          />
        </CardContent>
      </Card>

      {/* Quiet hours */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Moon className="w-4 h-4 text-[#AF52DE]" />
            Quiet hours
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <PrefRow
            icon={Moon} color="#AF52DE"
            title="Enable quiet hours"
            desc="Suppress all pushes during this window (nothing is lost — unread counts still update)"
            checked={prefs.quiet_hours.enabled}
            onChange={(v) => patchQH({ enabled: v })}
            testId="pref-quiet"
          />
          {prefs.quiet_hours.enabled && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-1" data-testid="quiet-hours-inputs">
              <div>
                <Label className="text-xs text-[#6B7280]">Start</Label>
                <Input
                  type="time"
                  value={prefs.quiet_hours.start}
                  onChange={e => patchQH({ start: e.target.value })}
                  data-testid="quiet-start"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">End</Label>
                <Input
                  type="time"
                  value={prefs.quiet_hours.end}
                  onChange={e => patchQH({ end: e.target.value })}
                  data-testid="quiet-end"
                />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280]">Local offset</Label>
                <Input
                  value={formatOffset(prefs.quiet_hours.timezone_offset_minutes)}
                  disabled
                  className="bg-[#F9FAFB]"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#002FA7]" data-testid="save-prefs-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save preferences
        </Button>
      </div>
    </div>
  );
};

const PrefRow = ({ icon: Icon, color, title, desc, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-3 border-b border-[#F3F4F6] pb-3 last:border-b-0 last:pb-0">
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <p className="text-sm font-medium text-[#111827]">{title}</p>
        <p className="text-xs text-[#6B7280] mt-0.5">{desc}</p>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

function formatOffset(mins) {
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default NotificationPrefsPage;
